<template>
    <main class="wb-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">
                <router-link to="/company/webhooks" class="tbv2-crumb">{{ $t('menu.companies.webhooks') }}</router-link>
                <span class="tbv2-sep">/</span>
                <span class="tbv2-current">{{ title }}</span>
            </div>
        </portal>

        <header class="wb-hero">
            <router-link to="/company/webhooks" class="wb-back">
                <i class="fal fa-arrow-left"></i>
                <span>{{ $t('common.back') || 'Retour' }}</span>
            </router-link>
            <h1 class="wb-hero-title">{{ title }}</h1>
            <p class="wb-hero-sub">{{ subtitle }}</p>
        </header>

        <section class="wb-card">
            <header class="wb-card-header">
                <div class="wb-card-icon">
                    <i class="fal fa-plug"></i>
                </div>
                <div>
                    <h2 class="wb-card-title">{{ $t('companies.webhook.create') }}</h2>
                    <p class="wb-card-desc">{{ subtitle }}</p>
                </div>
            </header>

            <form class="wb-form" @submit.prevent="doCreate">
                <div class="wb-field">
                    <label class="wb-label" for="webhook-name">{{ $t('companies.name') }}</label>
                    <input
                        id="webhook-name"
                        v-model="webhook.name"
                        type="text"
                        class="wb-input"
                        :placeholder="$t('companies.webhook.create_one')"
                    />
                </div>

                <div class="wb-field">
                    <label class="wb-label" for="webhook-url">URL</label>
                    <input
                        id="webhook-url"
                        v-model="webhook.url"
                        type="url"
                        class="wb-input"
                        placeholder="https://"
                    />
                    <span class="wb-hint">{{ $t('companies.webhook.url_hint') }}</span>
                </div>

                <div class="wb-field">
                    <label class="wb-label" for="webhook-secret">{{ $t('companies.secret') }}</label>
                    <input
                        id="webhook-secret"
                        v-model="webhook.signature_secret"
                        type="text"
                        class="wb-input wb-input--mono"
                    />
                    <span class="wb-hint">{{ $t('companies.webhook.secret_hint') }}</span>
                </div>

                <div v-if="!saved" class="wb-actions">
                    <router-link to="/company/webhooks" class="wb-btn wb-btn--ghost">
                        {{ $t('common.cancel') || 'Annuler' }}
                    </router-link>
                    <button
                        type="submit"
                        class="wb-btn wb-btn--primary"
                        :disabled="loading"
                    >
                        <i v-if="loading" class="fas fa-circle-notch fa-spin"></i>
                        <i v-else class="fal fa-check"></i>
                        {{ submitButtonText }}
                    </button>
                </div>
            </form>
        </section>
    </main>
</template>

<script>
import { mapActions, mapState } from 'vuex'

export default {
    name: 'WebhookBuilder',
    data() {
        const webhookId = this.$route.params.webhookId
        const isEdit = !!webhookId
        return {
            loading: false,
            saved: false,
            isEdit
        }
    },
    computed: {
        ...mapState('company', {
            webhook: (state) => state.newWebhook
        }),
        title() {
            return this.isEdit
                ? this.$t('companies.webhook.edit')
                : this.$t('companies.webhook.create')
        },
        subtitle() {
            return this.isEdit
                ? this.$t('companies.webhook.edit_subtitle')
                : this.$t('companies.webhook.create_subtitle')
        },
        submitButtonText() {
            return this.isEdit
                ? this.$t('companies.webhook.edit_webhook')
                : this.$t('companies.webhook.create_webhook')
        }
    },
    created() {
        const webhookId = this.$route.params.webhookId
        if (webhookId) {
            this.loading = true
            this.editWebhook(webhookId)
                .catch((e) => {
                    this.$store.dispatch('dispatchError', e)
                })
                .finally(() => {
                    this.loading = false
                })
        }
    },
    methods: {
        ...mapActions('company', {
            createWebhook: 'createWebhook',
            updateWebhook: 'updateWebhook',
            editWebhook: 'editWebhook'
        }),
        async doCreate() {
            this.loading = true
            const request = this.isEdit ? this.updateWebhook() : this.createWebhook()
            request
                .then(() => {
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('success.title'),
                        message: this.$t('success.message')
                    })
                    this.$router.push('/company/webhooks')
                })
                .catch((e) => {
                    this.$store.dispatch('dispatchError', e)
                })
                .finally(() => {
                    this.loading = false
                })
        }
    },
    beforeDestroy() {
        this.$store.commit('company/setNewWebhook', {
            name: null,
            url: null,
            signature_secret: null
        })
    }
}
</script>

<style scoped>
.wb-v2 {
    width: 100%;
    max-width: 720px;
    margin: 0 auto;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.tbv2-crumb {
    color: var(--v2-text-3);
    text-decoration: none !important;
    font-weight: var(--v2-font-weight-medium);
    transition: color var(--v2-transition-fast);
}

.tbv2-crumb:hover {
    color: var(--v2-primary);
}

.tbv2-sep {
    color: var(--v2-text-4);
}

.tbv2-current {
    color: var(--v2-text-1);
}

.wb-hero {
    margin-bottom: var(--v2-space-5);
}

.wb-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    text-decoration: none !important;
    margin-bottom: 10px;
    transition: color var(--v2-transition-fast);
}

.wb-back:hover {
    color: var(--v2-primary);
}

.wb-back i {
    font-size: 0.75rem;
}

.wb-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.wb-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
    max-width: 600px;
}

.wb-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
}

.wb-card-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 22px;
    border-bottom: 1px solid var(--v2-border);
}

.wb-card-icon {
    width: 40px;
    height: 40px;
    border-radius: var(--v2-radius-md);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 1.05rem;
}

.wb-card-title {
    margin: 0;
    font-size: 1rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
}

.wb-card-desc {
    margin: 2px 0 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
}

.wb-form {
    padding: 22px;
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.wb-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.wb-label {
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-2);
    letter-spacing: 0.01em;
    margin: 0;
}

.wb-input {
    width: 100%;
    box-sizing: border-box;
    height: 38px;
    padding: 0 12px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-1);
    font-family: inherit;
    outline: none;
    transition: border-color var(--v2-transition-fast), box-shadow var(--v2-transition-fast);
}

.wb-input:hover {
    border-color: var(--v2-border-strong);
}

.wb-input:focus {
    border-color: var(--v2-primary);
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.12);
}

.wb-input::placeholder {
    color: var(--v2-text-4);
}

.wb-input--mono {
    font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
    font-size: 0.82rem;
    letter-spacing: 0;
}

.wb-hint {
    font-size: 0.72rem;
    color: var(--v2-text-3);
    line-height: 1.4;
}

.wb-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 14px;
    border-top: 1px solid var(--v2-border);
    margin-top: 4px;
}

.wb-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 36px;
    padding: 0 16px;
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    border: 1px solid transparent;
    cursor: pointer;
    text-decoration: none !important;
    transition: all var(--v2-transition-fast);
    font-family: inherit;
}

.wb-btn i {
    font-size: 0.8rem;
}

.wb-btn--ghost {
    background: transparent;
    border-color: var(--v2-border);
    color: var(--v2-text-2);
}

.wb-btn--ghost:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.wb-btn--primary {
    background: var(--v2-primary);
    color: #fff !important;
}

.wb-btn--primary i {
    color: #fff !important;
}

.wb-btn--primary:hover:not(:disabled) {
    background: var(--v2-primary-hover, #0e7490);
    box-shadow: var(--v2-shadow-sm);
}

.wb-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

@media (max-width: 900px) {
    .wb-v2 {
        padding: 16px 18px 32px;
    }

    .wb-hero-title {
        font-size: 1.375rem;
    }

    .wb-form {
        padding: 16px;
    }

    .wb-card-header {
        padding: 14px 16px;
    }

    .wb-actions {
        flex-direction: column-reverse;
        align-items: stretch;
    }

    .wb-btn {
        justify-content: center;
    }
}
</style>
